/**
 * Asset Library Page
 *
 * Displays a grid of tenant assets with upload, search, and management capabilities.
 * Owner-only access.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import TopBar from '../../components/TopBar';
import { useTenant } from '../../contexts/TenantContext';
import { assetService, Asset, AssetCategory } from '../../services/assetService';
import { prepareImageForUpload, formatFileSize, IMAGE_CONFIG } from '../../utils/imageUtils';


interface AssetLibraryProps {
  tenantId: string;
  apiBaseUrl: string;
  authToken: string;
}

export const AssetLibrary: React.FC<AssetLibraryProps> = ({ tenantId, apiBaseUrl, authToken }) => {
  const { currentTenant } = useTenant();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<AssetCategory | ''>('');

  // Selected asset for details panel
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ displayName: '', description: '', tags: '' });

  // Upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadCategory, setUploadCategory] = useState<AssetCategory>('thumbnails');

  // Initialize auth token
  useEffect(() => {
    assetService.setAccessToken(authToken);
  }, [authToken]);

  // Fetch assets
  useEffect(() => {
    fetchAssets();
  }, [tenantId, categoryFilter, searchQuery]);

  const fetchAssets = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await assetService.listAssets(tenantId, {
        category: categoryFilter || undefined,
        search: searchQuery || undefined,
      });
      setAssets(response.assets || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError(null);

    const errors: string[] = [];
    let successCount = 0;

    try {
      for (const file of Array.from(files)) {
        try {
          // Validate and process the image (resize if needed)
          const processed = await prepareImageForUpload(file, uploadCategory);

          console.log(`📸 Processed ${file.name}: ${formatFileSize(file.size)} → ${formatFileSize(processed.size)} (${processed.width}x${processed.height})`);

          await assetService.uploadAsset(tenantId, {
            dataUrl: processed.dataUrl,
            filename: file.name,
            displayName: file.name.replace(/\.[^/.]+$/, ''),
            category: uploadCategory,
          });
          successCount++;
        } catch (fileErr: any) {
          errors.push(`${file.name}: ${fileErr.message}`);
        }
      }

      fetchAssets();

      if (errors.length > 0) {
        const errorMsg = errors.length === 1
          ? errors[0]
          : `${errors.length} files failed:\n${errors.join('\n')}`;
        setError(errorMsg);
      }

      if (successCount > 0 && errors.length > 0) {
        // Partial success
        console.log(`✅ Uploaded ${successCount} files, ${errors.length} failed`);
      }
    } catch (err: any) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle asset selection
  const handleAssetClick = (asset: Asset) => {
    setSelectedAsset(asset);
    setEditMode(false);
    setEditForm({
      displayName: asset.displayName,
      description: asset.description || '',
      tags: asset.tags?.join(', ') || '',
    });
  };

  // Handle edit save
  const handleSaveEdit = async () => {
    if (!selectedAsset) return;

    try {
      const updatedAsset = await assetService.updateAsset(
        selectedAsset.assetId,
        {
          displayName: editForm.displayName,
          description: editForm.description || undefined,
          tags: editForm.tags ? editForm.tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
        },
        tenantId
      );
      setSelectedAsset(updatedAsset);
      setEditMode(false);
      fetchAssets();
    } catch (err: any) {
      setError(`Update failed: ${err.message}`);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedAsset) return;
    if (!window.confirm(`Delete "${selectedAsset.displayName}"? This cannot be undone.`)) return;

    try {
      await assetService.deleteAsset(selectedAsset.assetId, tenantId);
      setSelectedAsset(null);
      fetchAssets();
    } catch (err: any) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  // formatFileSize is now imported from imageUtils

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleDateString();
  };

  const tenantName = currentTenant?.name || 'Tenant';

  const selCls = 'px-3 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast select-input';
  const inCls  = 'w-full px-3 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast placeholder:text-gray-400';
  const lbl    = 'block text-label font-medium text-gray-700 mb-1';
  const secBtn = 'px-4 py-2.5 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast';
  const priBtn = 'flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast';
  const dlRow  = 'flex gap-3 py-2 border-b border-gray-100 last:border-0';

  return (
    <div className="min-h-screen bg-surface-app">
      <TopBar tenantName={tenantName} userEmail="" currentJob={null} />

      <div className="px-8 py-6 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">Asset Library</h1>
          <div className="flex items-center gap-3">
            <select aria-label="Upload category" value={uploadCategory}
              onChange={e => setUploadCategory(e.target.value as AssetCategory)} className={selCls}>
              <option value="thumbnails">Thumbnails</option>
              <option value="references">References</option>
              <option value="icons">Icons</option>
              <option value="general">General</option>
            </select>
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast shadow-sm disabled:opacity-50">
              {uploading ? 'Uploading…' : 'Upload Assets'}
            </button>
            <input type="file" ref={fileInputRef} onChange={handleFileSelect}
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" multiple
              className="hidden" aria-label="Upload asset files" />
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 items-center mb-5">
          <input type="text" placeholder="Search assets…" value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast placeholder:text-gray-400" />
          <select aria-label="Filter by category" value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value as AssetCategory | '')} className={selCls}>
            <option value="">All Categories</option>
            <option value="thumbnails">Thumbnails</option>
            <option value="references">References</option>
            <option value="icons">Icons</option>
            <option value="general">General</option>
          </select>
        </div>

        {error && <div className="mb-4 px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label">{error}</div>}

        {/* Main layout */}
        <div className="flex gap-6 min-h-0">
          {/* Asset Grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="py-16 text-center text-body text-gray-400">Loading assets…</div>
            ) : assets.length === 0 ? (
              <div className="py-16 text-center">
                <div className="text-5xl mb-4">📁</div>
                <p className="text-body text-gray-500 mb-5">No assets found</p>
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast">
                  Upload Your First Asset
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {assets.map(asset => {
                  const isSel = selectedAsset?.assetId === asset.assetId;
                  return (
                    <button key={asset.assetId} type="button" onClick={() => handleAssetClick(asset)}
                      className={`text-left rounded-lg border-2 overflow-hidden transition-all duration-base bg-white ${
                        isSel ? 'border-blue-500 shadow-focus' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      }`}>
                      <div className="aspect-square bg-gray-100 overflow-hidden">
                        <img src={asset.url} alt={asset.displayName} loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      <div className="p-2.5">
                        <div className="text-label font-medium text-gray-900 truncate">{asset.displayName}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-meta font-medium capitalize">{asset.category}</span>
                          <span className="text-meta text-gray-400">{formatFileSize(asset.size)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Details Panel */}
          {selectedAsset && (
            <div className="w-72 flex-shrink-0 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h2 className="text-body font-semibold text-gray-900">Asset Details</h2>
                <button type="button" onClick={() => setSelectedAsset(null)}
                  className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-fast text-lg">×</button>
              </div>
              <div className="aspect-video bg-gray-100 overflow-hidden">
                <img src={selectedAsset.url} alt={selectedAsset.displayName} className="w-full h-full object-contain" />
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                {!editMode ? (
                  <div className="flex flex-col">
                    {[['Name', selectedAsset.displayName], ['Filename', <span className="font-mono text-meta">{selectedAsset.filename}</span>],
                      ['Category', <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-meta font-medium capitalize">{selectedAsset.category}</span>],
                      ['Size', formatFileSize(selectedAsset.size)], ['Type', selectedAsset.mimeType],
                      ['Uploaded', formatDate(selectedAsset.uploadedAt)],
                    ].map(([l, v]) => (
                      <div key={l as string} className={dlRow}>
                        <span className="text-meta font-medium text-gray-500 w-20 flex-shrink-0">{l as string}</span>
                        <span className="text-label text-gray-900 flex-1 min-w-0 break-all">{v as React.ReactNode}</span>
                      </div>
                    ))}
                    {selectedAsset.description && (
                      <div className={dlRow}>
                        <span className="text-meta font-medium text-gray-500 w-20 flex-shrink-0">Desc</span>
                        <span className="text-label text-gray-900 flex-1">{selectedAsset.description}</span>
                      </div>
                    )}
                    {selectedAsset.tags && selectedAsset.tags.length > 0 && (
                      <div className={dlRow}>
                        <span className="text-meta font-medium text-gray-500 w-20 flex-shrink-0">Tags</span>
                        <div className="flex flex-wrap gap-1">
                          {selectedAsset.tags.map(tag => <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-meta">{tag}</span>)}
                        </div>
                      </div>
                    )}
                    <div className={dlRow}>
                      <span className="text-meta font-medium text-gray-500 w-20 flex-shrink-0">Ref</span>
                      <code className="text-meta font-mono text-blue-600 break-all flex-1">asset://{selectedAsset.assetId}</code>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button type="button" onClick={() => setEditMode(true)} className={secBtn}>Edit</button>
                      <button type="button" onClick={handleDelete}
                        className="flex-1 py-2.5 bg-error-light text-error border border-error/30 rounded-md text-label font-medium hover:bg-red-100 transition-colors duration-fast">
                        Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    <div><label htmlFor="edit-display-name" className={lbl}>Display Name</label>
                      <input id="edit-display-name" type="text" value={editForm.displayName}
                        onChange={e => setEditForm({...editForm, displayName: e.target.value})} className={inCls} /></div>
                    <div><label htmlFor="edit-description" className={lbl}>Description</label>
                      <textarea id="edit-description" value={editForm.description}
                        onChange={e => setEditForm({...editForm, description: e.target.value})} rows={3}
                        className={`${inCls} resize-y`} /></div>
                    <div><label className={lbl}>Tags (comma-separated)</label>
                      <input type="text" value={editForm.tags}
                        onChange={e => setEditForm({...editForm, tags: e.target.value})}
                        placeholder="tag1, tag2, tag3" className={inCls} /></div>
                    <div className="flex gap-2">
                      <button type="button" onClick={handleSaveEdit} className={priBtn}>Save</button>
                      <button type="button" onClick={() => setEditMode(false)} className={secBtn}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetLibrary;

